import { createContext, useContext, useMemo } from 'react';

import { ChannelManager } from 'app/typings/channelManagers';
import { RateMode } from 'app/typings/rateModes';

import { HotelContext } from '../HotelContext/HotelContext';
import { LegacyHotelContext } from '../HotelContext/LegacyHotelContext';

type ExtranetFeatureAccessContextType = {
  isConnectedHotel: boolean;
  isAutomatedHotel: boolean;
  canEdit: boolean;
  canAccessBulkEdit: boolean;
  canAccessAutoInventory: (packageId: number) => boolean;
};

type Props = {
  children: React.ReactNode;
};

export const ExtranetFeatureAccessContext =
  createContext<ExtranetFeatureAccessContextType>({
    isConnectedHotel: false,
    isAutomatedHotel: false,
    canEdit: false,
    canAccessBulkEdit: false,
    canAccessAutoInventory: () => false,
  });

export const ExtranetFeatureAccessContextProvider = ({ children }: Props) => {
  const { hotelInfo } = useContext(HotelContext);
  const { isDayPackage } = useContext(LegacyHotelContext);

  const { channelManager, rateMode } = hotelInfo || {};

  const isConnectedHotel =
    !!channelManager && channelManager !== ChannelManager.NONE;

  const isAutomatedHotel = hotelInfo?.stockScrapperEnabled || false;

  const value = useMemo(
    () => ({
      isConnectedHotel,
      isAutomatedHotel,
      canEdit: !isConnectedHotel,
      canAccessBulkEdit: !isConnectedHotel || rateMode === RateMode.SINGLE_RATE,
      canAccessAutoInventory: (packageId: number) =>
        !isConnectedHotel && isDayPackage(packageId),
    }),
    [isConnectedHotel, isAutomatedHotel, rateMode, isDayPackage]
  );

  return (
    <ExtranetFeatureAccessContext.Provider value={value}>
      {children}
    </ExtranetFeatureAccessContext.Provider>
  );
};
